import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const MODES = [
  { value: "early_access", label: "Early access", desc: "Public CTA reads “Join Early Access”." },
  { value: "tickets_live", label: "Tickets live", desc: "Public CTA switches to “Get tickets” (still opens the signup form for now)." },
  { value: "waitlist", label: "Waitlist", desc: "Public CTA switches to “Join the waitlist”." },
];

const EMPTY = {
  mode: "early_access",
  date_label: "",
  venue: "",
  positioning: "",
  hero_eyebrow: "",
  hero_headline: "",
  hero_subheadline: "",
  hero_meta_date_value: "",
  hero_meta_date_note: "",
  hero_meta_duration_value: "",
  hero_meta_duration_note: "",
  hero_meta_venue_value: "",
  hero_meta_venue_note: "",
  hero_meta_audience_value: "",
  hero_meta_audience_note: "",
  hero_partners_label: "",
};

const INPUT_CLS = "adm-input";

function Field({ label, name, value, onChange, testid, hint, rows }) {
  return (
    <label className="adm-label">
      {label}
      {rows ? (
        <textarea
          name={name}
          value={value}
          onChange={onChange}
          rows={rows}
          className={INPUT_CLS}
          data-testid={testid}
        />
      ) : (
        <input
          name={name}
          value={value}
          onChange={onChange}
          className={INPUT_CLS}
          data-testid={testid}
        />
      )}
      {hint ? <span className="text-[11px] text-neutral-400">{hint}</span> : null}
    </label>
  );
}

export default function SettingsAdmin() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api
      .get("/admin/settings")
      .then((r) => {
        const d = r.data || {};
        setForm({
          ...EMPTY,
          ...Object.keys(EMPTY).reduce((acc, k) => {
            acc[k] = d[k] ?? EMPTY[k];
            return acc;
          }, {}),
          mode: d.mode || "early_access",
        });
      })
      .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/admin/settings", form);
      toast.success("Settings saved");
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-sm text-neutral-500">Loading…</p>;

  return (
    <div data-testid="settings-admin">
      <header className="mb-7">
        <p className="adm-eyebrow mb-1.5">Nettside</p>
        <h1 className="adm-title">Event settings</h1>
        <p className="adm-sub mt-1.5">Changes take effect on the public page immediately.</p>
      </header>

      <form onSubmit={save} className="max-w-3xl flex flex-col gap-5" data-testid="settings-admin-form">
        {/* ---- Event mode ---- */}
        <section className="adm-card p-6">
          <h2 className="adm-eyebrow mb-4">Event mode</h2>
          <div className="flex flex-col gap-3">
            {MODES.map((m) => (
              <label
                key={m.value}
                className={`flex items-start gap-3 p-3.5 border rounded-[10px] cursor-pointer transition-colors ${
                  form.mode === m.value ? "border-[#0f1115] bg-[#F7F5EE]" : "border-[#E7E4DB] hover:bg-[#FBFAF7]"
                }`}
                data-testid={`settings-mode-${m.value}`}
              >
                <input
                  type="radio"
                  name="mode"
                  value={m.value}
                  checked={form.mode === m.value}
                  onChange={onChange}
                  className="mt-1 accent-[#0f1115]"
                />
                <div>
                  <div className="text-sm font-medium">{m.label}</div>
                  <div className="text-xs text-neutral-500">{m.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </section>

        {/* ---- Hero copy ---- */}
        <section className="adm-card p-6">
          <h2 className="adm-eyebrow mb-4">Hero copy</h2>
          <div className="flex flex-col gap-4">
            <Field
              label="Eyebrow (above the headline)"
              name="hero_eyebrow"
              value={form.hero_eyebrow}
              onChange={onChange}
              testid="settings-input-hero-eyebrow"
              hint="Rendered uppercase with a single accent dot, e.g. “Spring 2027 • Grieghallen, Bergen”."
            />
            <Field
              label="Headline"
              name="hero_headline"
              value={form.hero_headline}
              onChange={onChange}
              testid="settings-input-hero-headline"
              rows={2}
              hint="Keep the closing period — it is the only element rendered in the accent colour."
            />
            <Field
              label="Sub-headline"
              name="hero_subheadline"
              value={form.hero_subheadline}
              onChange={onChange}
              testid="settings-input-hero-subheadline"
              rows={3}
            />
          </div>
        </section>

        {/* ---- Hero metadata panel ---- */}
        <section className="adm-card p-6">
          <h2 className="adm-eyebrow mb-1">Hero metadata panel</h2>
          <p className="text-[11px] text-neutral-400 mb-4">
            Four columns at the bottom of the hero. Leave a note empty to hide that second line.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Date — value" name="hero_meta_date_value" value={form.hero_meta_date_value} onChange={onChange} testid="settings-input-meta-date-value" />
            <Field label="Date — note" name="hero_meta_date_note" value={form.hero_meta_date_note} onChange={onChange} testid="settings-input-meta-date-note" />
            <Field label="Duration — value" name="hero_meta_duration_value" value={form.hero_meta_duration_value} onChange={onChange} testid="settings-input-meta-duration-value" />
            <Field label="Duration — note" name="hero_meta_duration_note" value={form.hero_meta_duration_note} onChange={onChange} testid="settings-input-meta-duration-note" />
            <Field label="Venue — value" name="hero_meta_venue_value" value={form.hero_meta_venue_value} onChange={onChange} testid="settings-input-meta-venue-value" />
            <Field label="Venue — note" name="hero_meta_venue_note" value={form.hero_meta_venue_note} onChange={onChange} testid="settings-input-meta-venue-note" />
            <Field label="Location — value" name="hero_meta_audience_value" value={form.hero_meta_audience_value} onChange={onChange} testid="settings-input-meta-audience-value" />
            <Field label="Location — note" name="hero_meta_audience_note" value={form.hero_meta_audience_note} onChange={onChange} testid="settings-input-meta-audience-note" />
          </div>
        </section>

        {/* ---- Partner strip + shared labels ---- */}
        <section className="adm-card p-6">
          <h2 className="adm-eyebrow mb-4">Partner strip &amp; shared labels</h2>
          <div className="flex flex-col gap-4">
            <Field
              label="Hero partner strip label"
              name="hero_partners_label"
              value={form.hero_partners_label}
              onChange={onChange}
              testid="settings-input-partners-label"
              hint="Shown with a “Partner with us →” link until a partner is marked confirmed. Confirmed partners replace it automatically."
            />
            <Field
              label="Date label (footer + program framing)"
              name="date_label"
              value={form.date_label}
              onChange={onChange}
              testid="settings-input-date-label"
            />
            <Field label="Venue" name="venue" value={form.venue} onChange={onChange} testid="settings-input-venue" />
            <Field
              label="Positioning line"
              name="positioning"
              value={form.positioning}
              onChange={onChange}
              testid="settings-input-positioning"
              rows={3}
            />
          </div>
        </section>

        <div>
          <button
            type="submit"
            disabled={saving}
            className="adm-btn"
            data-testid="settings-admin-save"
          >
            {saving ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>
    </div>
  );
}
